import { motion } from 'framer-motion';
import { Truck, Package } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { EquipmentCard } from '../equipment';
import { formatCurrency } from '../../utils';

export function EquipmentSection() {
  const proposal = useProposalData();

  if (!proposal.sections.showEquipment || proposal.equipment.length === 0) {
    return null;
  }

  const totalUnits = proposal.equipment.reduce((sum, eq) => sum + eq.quantity, 0);
  // Options are already included in unitPrice - don't add them again
  const equipmentTotal = proposal.equipment.reduce((sum, eq) => {
    return sum + (eq.extended || (eq.unitPrice * eq.quantity));
  }, 0);

  return (
    <Section id="equipment" className="bg-gray-50" pattern="diagonal">
      {/* Summary Bar */}
      <motion.div
        className="mb-12 -mt-8 p-6 bg-white rounded-xl shadow-lg border-l-4 border-hul-red"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-14 h-14 bg-hul-red/10 rounded-xl flex items-center justify-center">
              <Truck className="w-7 h-7 text-hul-red" />
            </div>
            <div>
              <div className="text-sm text-gray-500 uppercase tracking-wider">
                Equipment Summary
              </div>
              <div className="font-heading text-2xl text-hul-navy">
                {proposal.equipment.length} Line{proposal.equipment.length !== 1 ? 's' : ''} · {totalUnits} Unit{totalUnits !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-1">Equipment Total</div>
            <div className="font-mono text-3xl font-bold text-hul-red">
              {formatCurrency(equipmentTotal)}
            </div>
          </div>
        </div>
      </motion.div>

      <SectionHeader
        title="YOUR EQUIPMENT"
        subtitle="Selected Units"
      />

      {/* Equipment Cards */}
      <div className="space-y-8">
        {proposal.equipment.map((equipment, index) => (
          <EquipmentCard
            key={equipment.lineNumber}
            equipment={equipment}
            index={index}
          />
        ))}
      </div>

      {/* Equipment Legend */}
      <motion.div
        className="mt-12 p-6 bg-hul-navy/5 rounded-xl"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-gray-600">New Equipment</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-500 rounded-full" />
            <span className="text-gray-600">Certified Pre-Owned</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-amber-500 rounded-full" />
            <span className="text-gray-600">Used Equipment</span>
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Options included in price</span>
          </div>
        </div>
      </motion.div>
    </Section>
  );
}
